// src/pages/ShopPage.js – Cinematic enhanced
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { shopAPI } from '../utils/api';
import { useSound } from '../hooks/useSound';
import { useCinematic } from '../hooks/useCinematic';
import useViewport from '../hooks/useViewport';

const LOCAL_ITEMS = [
  { id:'theme_cyber',    type:'theme',       name:'Cyber Punk',    desc:'Electric blue neon',     price:200, icon:'💜' },
  { id:'theme_matrix',   type:'theme',       name:'Matrix Mode',   desc:'Green hacker aesthetic', price:300, icon:'💚' },
  { id:'theme_sunset',   type:'theme',       name:'Sunset',        desc:'Warm orange-pink',       price:250, icon:'🌅' },
  { id:'theme_ocean',    type:'theme',       name:'Deep Ocean',    desc:'Cool teal and navy',     price:250, icon:'🌊' },
  { id:'avatar_robot',   type:'avatar',      name:'Robot Coder',   desc:'Friendly coding robot',  price:150, icon:'🤖' },
  { id:'avatar_ninja',   type:'avatar',      name:'Code Ninja',    desc:'Silent but deadly',      price:200, icon:'🥷' },
  { id:'avatar_alien',   type:'avatar',      name:'Space Hacker',  desc:'From another galaxy',    price:300, icon:'👾' },
  { id:'avatar_wizard',  type:'avatar',      name:'Code Wizard',   desc:'Arcane arts of code',    price:350, icon:'🧙‍♂️' },
  { id:'avatar_dragon',  type:'avatar',      name:'Dragon Dev',    desc:'Legendary coder',        price:500, icon:'🐉' },
  { id:'skin_monokai',   type:'editor_skin', name:'Monokai Pro',   desc:'Classic Monokai colors', price:100, icon:'🎨' },
  { id:'skin_dracula',   type:'editor_skin', name:'Dracula',       desc:'Dark vampire theme',     price:150, icon:'🧛' },
  { id:'skin_solarized', type:'editor_skin', name:'Solarized Dark',desc:'Easy on the eyes',       price:100, icon:'☀️' },
];

const TABS = [
  { id:'all',        label:'ALL'        },
  { id:'theme',      label:'🎨 THEMES'  },
  { id:'avatar',     label:'👤 AVATARS' },
  { id:'editor_skin',label:'💅 SKINS'   },
];

export default function ShopPage() {
  const { user, updateUser, showToast } = useAuth();
  const [items,   setItems]   = useState(LOCAL_ITEMS);
  const [filter,  setFilter]  = useState('all');
  const [buyingId,setBuyingId]= useState(null);
  const { playClick, playCoin, playError, playHover } = useSound();
  const { spawnButtonSparks, glowFlash } = useCinematic();
  const { isMobile } = useViewport();

  useEffect(() => {
    shopAPI.getItems().then(r => setItems(r.data.items || LOCAL_ITEMS)).catch(() => {});
  }, []);

  const isOwned   = id => (user?.inventory || []).some(i => (i.id||i.itemId) === id);
  const canAfford = price => (user?.coins || 0) >= price;

  const handleBuy = async (e, item) => {
    if (!user || buyingId) return;
    if (isOwned(item.id)) { showToast('Already owned!', `${item.name} is in your inventory`); return; }
    if (!canAfford(item.price)) {
      playError(); spawnButtonSparks(e, '#ff4444');
      showToast('Not enough coins!', `Need ${item.price - user.coins} more coins`, 'error'); return;
    }
    playClick(); spawnButtonSparks(e, '#ffd700');
    setBuyingId(item.id);
    try {
      await shopAPI.buy(item.id);
      const newInventory = [...(user.inventory||[]), { id:item.id, itemId:item.id, type:item.type, name:item.name }];
      const updates = { coins: user.coins - item.price, inventory: newInventory };
      if (item.type === 'avatar') updates.avatar = item.icon;
      updateUser(updates);
      playCoin(); glowFlash('rgba(255,215,0,0.12)', 450);
      showToast(`${item.name} Unlocked! 🎉`, `Spent ${item.price} coins`, 'success');
    } catch (err) {
      playError();
      showToast('Purchase failed', err?.response?.data?.message || 'Please try again', 'error');
    }
    setBuyingId(null);
  };

  const visible = filter === 'all' ? items : items.filter(i => i.type === filter);

  return (
    <div className="page-shell">
      <div className="section-title animate-fade-in">SHOP</div>

      <div className="animate-fade-in" style={{ display:'flex', alignItems:'center', gap:'14px', marginBottom:'18px', flexWrap:'wrap' }}>
        <div style={{ fontFamily:'var(--font-game)', fontSize:isMobile ? '20px' : '24px', fontWeight:700, color:'#ffd700', textShadow:'0 0 14px rgba(255,215,0,0.4)' }}>
          🪙 {user?.coins || 0}
        </div>
        <div style={{ fontSize:'15px', color:'rgba(255,255,255,0.3)' }}>Complete missions to earn more coins!</div>
      </div>

      <div className="animate-fade-in" style={{ display:'flex', gap:'7px', marginBottom:'20px', flexWrap:'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={e => { playClick(); setFilter(t.id); }} style={{
            padding:'7px 16px', borderRadius:'5px', cursor:'pointer',
            fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700, letterSpacing:'1px',
            border:`1px solid ${filter===t.id ? 'rgba(0,245,255,0.42)' : 'rgba(255,255,255,0.08)'}`,
            background: filter===t.id ? 'rgba(0,245,255,0.1)' : 'rgba(255,255,255,0.02)',
            color: filter===t.id ? '#00f5ff' : 'rgba(255,255,255,0.4)',
            transition:'all 0.18s',
            boxShadow: filter===t.id ? '0 0 14px rgba(0,245,255,0.14)' : 'none',
          }}
            onMouseEnter={() => playHover()}
          >{t.label}</button>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))', gap:'13px' }}>
        {visible.map((item, idx) => {
          const owned   = isOwned(item.id);
          const afford  = canAfford(item.price);
          const buying  = buyingId === item.id;
          return (
            <div key={item.id}
              onClick={e => !owned && handleBuy(e, item)}
              style={{
                background: owned ? 'rgba(57,255,20,0.05)' : 'rgba(13,17,23,0.9)',
                border:`1px solid ${owned?'rgba(57,255,20,0.28)':'rgba(0,245,255,0.1)'}`,
                borderRadius:'11px', padding:'18px', textAlign:'center',
                cursor: owned ? 'default' : afford ? 'pointer' : 'not-allowed',
                opacity: !owned && !afford ? 0.5 : 1,
                transition:'all 0.25s cubic-bezier(0.16,1,0.3,1)',
                backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)',
                boxShadow: owned ? '0 0 18px rgba(57,255,20,0.08)' : 'none',
                animationDelay:`${idx*0.04}s`,
              }}
              onMouseEnter={e => {
                playHover();
                if(!owned && afford){ e.currentTarget.style.transform='translateY(-4px) scale(1.01)'; e.currentTarget.style.borderColor='rgba(0,245,255,0.32)'; e.currentTarget.style.boxShadow='0 8px 28px rgba(0,0,0,0.5), 0 0 20px rgba(0,245,255,0.1)'; }
              }}
              onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.borderColor=owned?'rgba(57,255,20,0.28)':'rgba(0,245,255,0.1)'; e.currentTarget.style.boxShadow=owned?'0 0 18px rgba(57,255,20,0.08)':'none'; }}
            >
              <div style={{ fontSize:'38px', marginBottom:'9px', filter: owned?'drop-shadow(0 0 10px rgba(57,255,20,0.4))':'none' }}>{item.icon}</div>
              <div style={{ fontWeight:700, fontSize:'15px', color:'#fff', marginBottom:'4px' }}>{item.name}</div>
              <div style={{ fontSize:'15px', color:'rgba(255,255,255,0.38)', marginBottom:'12px', lineHeight:1.4 }}>{item.desc}</div>
              <div style={{ fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700,
                            color: owned?'#39ff14':afford?'#ffd700':'rgba(255,255,255,0.25)',
                            textShadow: owned?'0 0 8px rgba(57,255,20,0.4)':afford?'0 0 8px rgba(255,215,0,0.3)':'none' }}>
                {buying ? '...' : owned ? '✓ OWNED' : `🪙 ${item.price}`}
              </div>
              {!owned && !afford && (
                <div style={{ fontSize:'15px', color:'rgba(255,68,68,0.65)', marginTop:'3px', letterSpacing:'1px' }}>
                  NEED {item.price - (user?.coins||0)} MORE
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
